/**
 * Where to land after signing in: the private page that sent the visitor to
 * the login screen. Kept in this tab's sessionStorage — the token for a
 * session without "Remember me" lives there too, so a link opened in a new
 * tab asks to sign in and then continues to that link instead of Today.
 *
 * Read once and cleared. Only a same-site path is ever returned, so a crafted
 * value can never send anybody to another website.
 */

const STORAGE_KEY = 'auth.returnTo';
/** A sign-in abandoned for longer than this no longer means "continue there". */
const MAX_AGE_MS = 30 * 60 * 1000;
const MAX_PATH_LENGTH = 2048;

interface IStoredReturnTo {
    readonly path: string;
    readonly at: number;
}

/** A path on this site: one leading slash, not `//host` or `/\host`, no scheme. */
export function isSameSitePath(path: string): boolean {
    return (
        path.length <= MAX_PATH_LENGTH &&
        path.startsWith('/') &&
        !path.startsWith('//') &&
        !path.startsWith('/\\')
    );
}

export function rememberReturnTo(path: string): void {
    if (!isSameSitePath(path)) return;
    try {
        const stored: IStoredReturnTo = { path, at: Date.now() };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
        // Storage blocked (private mode): signing in still lands on the home page.
    }
}

export function forgetReturnTo(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing stored that could be read either.
    }
}

/** The remembered page, cleared as it is read; null when none, stale or unsafe. */
export function takeReturnTo(): string | null {
    let raw: string | null = null;
    try {
        raw = sessionStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
    forgetReturnTo();
    if (!raw) return null;
    try {
        const stored = JSON.parse(raw) as Partial<IStoredReturnTo>;
        if (typeof stored.path !== 'string' || typeof stored.at !== 'number') return null;
        if (Date.now() - stored.at > MAX_AGE_MS) return null;
        return isSameSitePath(stored.path) ? stored.path : null;
    } catch {
        return null;
    }
}
